/**
 * BreadcrumbBar — bande fil d'Ariane, rendue en haut du panel center.
 *
 * @remarks
 * Chrome shell agnostique au métier : lit les items publiés par la vue center
 * courante (`useBreadcrumb` → store partagé) et les rend.
 *
 * Une vue qui ne publie rien (les pages de premier niveau) retombe sur le titre
 * de sa route (`handle.title`) : la bande est sur toutes les pages, pour que
 * l'en-tête reste le même partout (SAAS-1346). Le repli vit ici, au rendu, et
 * pas dans le store : `useDocumentTitle` lit le store en priorité et garde son
 * propre repli.
 */

import { useMatches } from 'react-router-dom';
// Sous-chemins : le barrel réexporte Excalidraw, qui casse sous Vitest.
import { Breadcrumb, type BreadcrumbItem } from '@/shared/components/breadcrumb/Breadcrumb';
import { useBreadcrumbStore } from '@/shared/components/breadcrumb/use-breadcrumb-store';

/** Le titre de la route active, la plus profonde qui en déclare un. */
const useRouteTitle = (): string | null => {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i]?.handle;
    if (typeof handle === 'object' && handle !== null && 'title' in handle) {
      const { title } = handle;
      if (typeof title === 'string') return title;
    }
  }
  return null;
};

export function BreadcrumbBar() {
  const published = useBreadcrumbStore((s) => s.items);
  const actions = useBreadcrumbStore((s) => s.actions);
  const routeTitle = useRouteTitle();

  const items: readonly BreadcrumbItem[] =
    published.length > 0 ? published : routeTitle !== null ? [{ label: routeTitle }] : [];

  if (items.length === 0 && !actions) return null;

  // `--panel-header-height` = hauteur des barres d'onglets latérales (PanelTabs)
  // → bordure basse alignée entre les panneaux.
  // breadcrumb à gauche (flux normal), actions centrées en absolu.
  return (
    <div className="border-border bg-surface relative flex h-(--panel-header-height) shrink-0 items-center border-b px-4">
      <Breadcrumb items={[...items]} />
      {actions && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">{actions}</div>
      )}
    </div>
  );
}
